import React, { useEffect } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import InventoryIcon from '@mui/icons-material/Inventory';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import WarningIcon from '@mui/icons-material/Warning';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { palette } from '../../theme/colors';

interface ReservationItem {
  id: string;
  productId: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
}

interface PlannedTransfer {
  productId: string;
  productName: string;
  quantity: number;
  fromLocationId: string;
  fromLocationName: string;
  toLocationId: string;
  toLocationName: string;
}

interface DeliveryAddress {
  street: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  notes?: string;
}

interface Delivery {
  id: string;
  status: string;
  driverId?: string;
  driverName?: string;
  driverPhone?: string;
  estimatedDeliveryTime?: string;
  pickedUpAt?: string;
  deliveredAt?: string;
}

interface Reservation {
  id: string;
  reservationNumber: string;
  status: string;
  expiresAt: string;
  createdAt: string;
  items: ReservationItem[];
  totalAmount: number;
  quoteId?: string;
  location?: {
    name: string;
    type: string;
  };
  // Transfer and delivery fields
  plannedTransfers?: PlannedTransfer[];
  pendingTransferIds?: string[];
  deliveryAddress?: DeliveryAddress | null;
  deliveryId?: string;
  delivery?: Delivery;
}

interface PortalContext {
  isAuthenticated: boolean;
}

const fetchMyReservations = async (orgSlug: string, token: string): Promise<Reservation[]> => {
  const response = await fetch(
    `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/b2b-portal/${orgSlug}/my-reservations`,
    {
      headers: { 'Authorization': `Bearer ${token}` },
    }
  );
  if (!response.ok) {
    throw new Error('Failed to fetch reservations');
  }
  const data = await response.json();
  return Array.isArray(data) ? data : data.data || [];
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'confirmed':
    case 'completed':
    case 'delivered':
      return palette.success;
    case 'pending':
    case 'waiting_transfer':
    case 'waiting_admin_action':
      return palette.warning;
    case 'ready_for_pickup':
    case 'ready_for_delivery':
      return palette.primary;
    case 'out_for_delivery':
      return '#2196f3'; // Blue for in-transit
    case 'cancelled':
    case 'expired':
    case 'transfer_issue':
      return palette.error;
    default:
      return palette.primary;
  }
};

const getStatusLabel = (status: string) => {
  const labels: Record<string, string> = {
    pending: 'PENDING',
    waiting_transfer: 'AWAITING TRANSFER',
    transfer_issue: 'TRANSFER ISSUE',
    waiting_admin_action: 'PROCESSING',
    confirmed: 'CONFIRMED',
    ready_for_pickup: 'READY FOR PICKUP',
    ready_for_delivery: 'READY FOR DELIVERY',
    out_for_delivery: 'OUT FOR DELIVERY',
    completed: 'COMPLETED',
    cancelled: 'CANCELLED',
    expired: 'EXPIRED',
  };
  return labels[status] || status.toUpperCase().replace(/_/g, ' ');
};

const getStatusIcon = (status: string): React.ReactElement | undefined => {
  switch (status) {
    case 'waiting_transfer':
      return <SwapHorizIcon sx={{ fontSize: 14 }} />;
    case 'transfer_issue':
      return <WarningIcon sx={{ fontSize: 14 }} />;
    case 'waiting_admin_action':
      return <HourglassEmptyIcon sx={{ fontSize: 14 }} />;
    case 'out_for_delivery':
    case 'ready_for_delivery':
      return <LocalShippingIcon sx={{ fontSize: 14 }} />;
    default:
      return undefined;
  }
};

export const B2BReservationsPage: React.FC = () => {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useOutletContext<PortalContext>();
  const token = localStorage.getItem('access_token');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate(`/b2b/${orgSlug}/login`);
    }
  }, [isAuthenticated, navigate, orgSlug]);

  const { data: reservations, isLoading, error } = useQuery({
    queryKey: ['b2b-my-reservations', orgSlug],
    queryFn: () => fetchMyReservations(orgSlug!, token!),
    enabled: !!orgSlug && !!token,
  });

  return (
    <Box>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        <InventoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        My Reservations
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        View your stock reservations from accepted quotes
      </Typography>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          Failed to load reservations. Please try again.
        </Alert>
      )}

      {!isLoading && reservations?.length === 0 && (
        <Card sx={{ textAlign: 'center', py: 8 }}>
          <InventoryIcon sx={{ fontSize: 80, color: palette.textMuted, mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No reservations yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Convert an accepted quote to create a reservation
          </Typography>
          <Button
            variant="contained"
            startIcon={<RequestQuoteIcon />}
            onClick={() => navigate(`/b2b/${orgSlug}/quotes`)}
          >
            View My Quotes
          </Button>
        </Card>
      )}

      {reservations && reservations.length > 0 && (
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: palette.sky }}>
                  <TableCell sx={{ fontWeight: 600 }}>Reservation #</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Items</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Location</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Expires</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reservations.map((reservation) => (
                  <TableRow
                    key={reservation.id}
                    hover
                    sx={{ cursor: 'default' }}
                  >
                    <TableCell>
                      <Typography fontWeight={600}>{reservation.reservationNumber}</Typography>
                      {reservation.quoteId && (
                        <Typography variant="caption" color="text.secondary">
                          From Quote
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {reservation.items?.length || 0} item{(reservation.items?.length || 0) !== 1 ? 's' : ''}
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight={600} color={palette.primary}>
                        {(reservation.totalAmount || 0).toFixed(2)} DZD
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {reservation.location ? (
                        <Box>
                          <Typography variant="body2">{reservation.location.name}</Typography>
                          <Chip
                            label={reservation.location.type}
                            size="small"
                            sx={{ fontSize: 10, height: 18 }}
                          />
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {reservation.expiresAt ? (
                        <Typography
                          variant="body2"
                          color={
                            new Date(reservation.expiresAt) < new Date()
                              ? 'error'
                              : 'text.primary'
                          }
                        >
                          {new Date(reservation.expiresAt).toLocaleDateString()}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Chip
                          icon={getStatusIcon(reservation.status)}
                          label={getStatusLabel(reservation.status)}
                          size="small"
                          sx={{
                            bgcolor: `${getStatusColor(reservation.status)}20`,
                            color: getStatusColor(reservation.status),
                            fontWeight: 600,
                          }}
                        />
                        {/* Transfer progress indicator */}
                        {reservation.status === 'waiting_transfer' && reservation.pendingTransferIds && (
                          <Typography variant="caption" color="text.secondary">
                            <SwapHorizIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.5 }} />
                            {reservation.pendingTransferIds.length} transfer(s) pending
                          </Typography>
                        )}
                        {/* Delivery info */}
                        {reservation.delivery && reservation.delivery.status === 'out_for_delivery' && (
                          <Typography variant="caption" color="primary">
                            <LocalShippingIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.5 }} />
                            En route
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {new Date(reservation.createdAt).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}
    </Box>
  );
};
